import { isSportsVOD } from 'src/components/MetaButtons/WatchlistBtn/WatchlistHandler';
import { newKeysList } from 'src/components/pageMeta/SEOMetaUtils';
import { transformToCamelCase } from 'src/components/pageMeta/transformToCamelCase';
import { ContentTypes } from 'src/components/thumbnail/types';

import { getLocalStorage } from './browserStorageHandler';
import { checkKidsCard, globalUrlHelper, isServer } from './common';
import { DynamicObject, SpecialContentItemType } from './commonTypes';
import {
  ASSET_SUBTYPE,
  ASSET_TYPE,
  ExternalLinkEnums,
  USE_SLUG
} from './constants/AppConstants';
import { rangbaazDubbedPageIds } from './constants/CanonicalURLConstants';
import { EDUAURAA, PARTNER_NAME } from './constants/localStorageConstants';
import {
  COLLECTIONS_URL,
  NOT_FOUND,
  OLD_CHANNELCONSUMPTION_PATH
} from './constants/RouterConstants';
import {
  ConsumptionPageUrls,
  SubscriptionPageURL
} from './constants/stringConstants';
import { MATCH_CLIPS, SPORTS_ROUTE } from './constants/utilConstants';
import { eduauraaAppLink } from './eduauraaUtils';
import {
  getRangbaazDubbedPageUrl,
  isSportsLiveEvent,
  titleGenerator
} from './routeHandler';
import { MIXPANEL_EXTERNAL_LINK } from './analytics/mixpanel/mixpanelConstants';
import { mixpanelObject } from './analytics';

/**
 * TODO: Move to common utils
 * TODO: Add the datatype
 * Checks whether the data is a story or not
 */
export const isStoriesData = (data: any) => {
  return data &&
    ((Array.isArray(data.posts) && data.status) || data.permalink || data.link)
    ? true
    : false;
};

// TODO: useSlug from environment variable
const useSlug = USE_SLUG;
const ZEE5_DOT_COM = 'zee5.com';
const DOT_COM = '.com';

const aORb = <T>(a: T, b: T) => a || b;

const reduceOR =
  <T>(initVal: T, func: (a: T) => T) =>
  (arr: T[]) =>
    arr.reduce((b: T, c: T) => aORb<T>(b, func(c)), initVal);

const getTitle = reduceOR<string>('', titleGenerator);
const checkTitle = reduceOR<string>('', (a) => a);

const movieUrlGenerator = ({
  slug,
  web_url: url,
  title = '',
  originalTitle = '',
  id,
  seo_title: seoTitle = '',
  extended,
  genres,
  genre,
  contentPartner
}: DynamicObject) => {
  const genreArray = genres || genre || [];
  const isKidCard = checkKidsCard(genreArray);
  const routeParam = isKidCard
    ? ConsumptionPageUrls.KIDS_CONSUMPTION_MOVIES
    : ConsumptionPageUrls.MOVIES_CONSUMPTION;
  let contentUrl = '';
  if (contentPartner?.name) {
    contentUrl = `/add-ons/${contentPartner?.name
      .toLowerCase()
      .replace(' ', '-')}`;
  }
  if (title) {
    const generatedTitle = titleGenerator(originalTitle);
    return globalUrlHelper(
      `${contentUrl}/movies/details/${generatedTitle}/${id}`
    );
  }

  const slugORUrl = slug || url;
  if (slugORUrl) {
    // removes kids from slug if it is a kids card
    return globalUrlHelper(
      isKidCard ? `/${routeParam}/${slugORUrl.substring(15)}` : `/${slugORUrl}`
    );
  }

  const extendedSeoTitle = extended ? extended.seo_title : '';

  const titleArray = [originalTitle, title, seoTitle, extendedSeoTitle];
  if (checkTitle(titleArray) && id) {
    const movieTitle = getTitle(titleArray);
    return globalUrlHelper(`/${routeParam}/${movieTitle}/${id}`);
  }
  return NOT_FOUND;
};

export const externalUrlHandler = (
  link: string,
  mixpanelCallback: () => void
) => {
  mixpanelObject[MIXPANEL_EXTERNAL_LINK] = link || 'N/A';
  mixpanelCallback();
  window.open(link, '_blank');
};

const showUrlGenerator = ({
  slug,
  web_url: url,
  web_slug: webSlug,
  title = '',
  originalTitle,
  seo_title: seoTitle = '',
  id,
  asset,
  extended,
  genres,
  genre,
  contentPartner
}: DynamicObject) => {
  if (!(originalTitle || title || seoTitle || extended) || !id || !asset)
    return NOT_FOUND;
  let contentUrl = '';
  if (contentPartner?.name) {
    contentUrl = `/add-ons/${contentPartner?.name
      .toLowerCase()
      .replace(' ', '-')}`;
  }
  if (useSlug) {
    const webSlugORSlugOrUrl = webSlug || slug || url;
    if (webSlugORSlugOrUrl) {
      return globalUrlHelper(`/${webSlugORSlugOrUrl}`);
    }
  }

  const extendedSeoTitle = extended ? extended.seo_title : '';

  const titleArray = [originalTitle, extendedSeoTitle, seoTitle, title];
  if (checkTitle(titleArray) && id) {
    let showTitle = getTitle(titleArray);

    if (rangbaazDubbedPageIds.includes(id)) {
      showTitle = getRangbaazDubbedPageUrl(showTitle, id);
    }
    const genreArray = genres || genre || [];
    const isKidCard = checkKidsCard(genreArray);

    return globalUrlHelper(
      `${contentUrl}/${
        isKidCard ? ConsumptionPageUrls.KIDS_CONSUMPTION_SHOWS : asset
      }/${showTitle}/${id}`
    );
  }
  return NOT_FOUND;
};

const episodeUrlGenerator = ({
  slug,
  webUrl: url,
  webSlug,
  title = '',
  originalTitle: contentOriginalTitle,
  id,
  tvShow,
  seoTitle = '',
  extended,
  tvShowDetails,
  genre,
  genres,
  assetType,
  assetSubType
}: DynamicObject) => {
  if (
    !(contentOriginalTitle || title || extended || seoTitle) ||
    !id ||
    !(tvShow || tvShowDetails)
  )
    return NOT_FOUND;
  if (useSlug) {
    const webSlugORSlugOrUrl = webSlug || slug || url;
    if (webSlugORSlugOrUrl) {
      return globalUrlHelper(`/${webSlugORSlugOrUrl}`);
    }
  }

  const tvShowData = tvShow || tvShowDetails;

  if (tvShowData) {
    const { originalTitle, id: showId } = tvShowData;
    const extendedSeoTitle = extended ? extended.seoTitle : '';
    const titleArray = [
      extendedSeoTitle,
      seoTitle,
      contentOriginalTitle,
      title
    ];

    if (originalTitle && checkTitle(titleArray) && showId && id) {
      let showTitle = titleGenerator(originalTitle);
      const contentTitle = getTitle(titleArray);
      // default asset
      let asset: string = ConsumptionPageUrls.TVSHOWS_CONSUMPTION;

      if (assetSubType && assetSubType === ASSET_SUBTYPE.ORIGINALS) {
        asset = ConsumptionPageUrls.ZEE5ORIGINALSCONSUMPTION;
      } else if (
        isSportsLiveEvent({
          assetType,
          assetSubType
        })
      ) {
        asset = `${SPORTS_ROUTE}/live-streaming`;
        return globalUrlHelper(`/${asset}/${showId}/${contentTitle}/${id}`);
      } else if (
        isSportsVOD({
          assetSubType,
          assetType
        })
      ) {
        asset = `${SPORTS_ROUTE}`;
        showTitle = MATCH_CLIPS;
      }
      const isKidCard = checkKidsCard(genres || genre);
      if (isKidCard) {
        return globalUrlHelper(
          `/kids/kids-shows/${showTitle}/${showId}/${contentTitle}/${id}`
        );
      }
      return globalUrlHelper(
        `/${asset}/${showTitle}/${showId}/${contentTitle}/${id}`
      );
    }
  }
  return NOT_FOUND;
};

const videoUrlGenerator = (
  {
    slug,
    web_url: url,
    title = '',
    originalTitle,
    id,
    seo_title: seoTitle = '',
    extended,
    genre,
    genres
  }: DynamicObject,
  asset: string
) => {
  if (!(originalTitle || title || seoTitle || extended) || !id)
    return NOT_FOUND;
  if (useSlug) {
    const slugORUrl = slug || url;
    if (slugORUrl)
      return globalUrlHelper(`${slug ? `/${asset}` : ''}/${slugORUrl}`);
  }
  const extendedSeoTitle = extended ? extended.seo_title : '';
  const titleArray = [originalTitle, extendedSeoTitle, seoTitle, title];

  if (checkTitle(titleArray) && id) {
    const videoTitle = getTitle(titleArray);

    const genreArray = genre || genres || [];
    const isKidCard = checkKidsCard(genreArray);

    return globalUrlHelper(
      `/${
        isKidCard ? ConsumptionPageUrls.KIDS_CONSUMPTION_VIDEOS : asset
      }/${videoTitle}/${id}`
    );
  }
  return NOT_FOUND;
};

export const collectionUrlGenerator = ({
  slug,
  web_url: url,
  title = '',
  originalTitle,
  id,
  seo_title: seoTitle = '',
  extended,
  content_partner_details: { content_partner_name: partnerName = '' } = {}
}: DynamicObject) => {
  if (partnerName) {
    return globalUrlHelper(`/add-on/${partnerName.toLowerCase()}/${id}`);
  }
  if (!(originalTitle || title || seoTitle || extended) || !id)
    return NOT_FOUND;
  if (useSlug) {
    const slugORUrl = slug || url;
    if (slugORUrl) return globalUrlHelper(`/${slugORUrl}`);
  }

  const extendedSeoTitle = extended ? extended.seo_title : '';
  const titleArray = [originalTitle, extendedSeoTitle, seoTitle, title];

  if (checkTitle(titleArray) && id) {
    const collectionTitle = getTitle(titleArray);
    return globalUrlHelper(`${COLLECTIONS_URL}/${collectionTitle}/${id}`);
  }
  return NOT_FOUND;
};

const movieAssetHandler = (item: DynamicObject) => {
  const { assetSubType, genres, genre } = item;
  const genreArray = genres || genre || [];

  const findInGenre = (value: string) =>
    genreArray.find((genreItem: DynamicObject) =>
      genreItem?.id.toLowerCase().includes(value)
    );

  if (
    assetSubType?.toLowerCase() === ASSET_SUBTYPE.VIDEO &&
    findInGenre(ASSET_SUBTYPE.NEWS)
  ) {
    const asset = ConsumptionPageUrls.NEWS_CONSUMPTION;
    return videoUrlGenerator(item, asset);
  } else if (
    assetSubType?.toLowerCase() === ASSET_SUBTYPE.VIDEO &&
    findInGenre(ASSET_SUBTYPE.MUSIC)
  ) {
    const asset = ConsumptionPageUrls.MUSICVIDEOS_CONSUMPTION;
    return videoUrlGenerator(item, asset);
  } else if (assetSubType?.toLowerCase() === ASSET_SUBTYPE.MOVIE) {
    return movieUrlGenerator(item);
  } else {
    const asset = ConsumptionPageUrls.VIDEOS_CONSUMPTION;
    return videoUrlGenerator(item, asset);
  }
};

const showAssetHandler = (item: DynamicObject) => {
  const { assetSubType } = item;
  let url = NOT_FOUND;

  if (
    assetSubType?.toLowerCase() === ASSET_SUBTYPE.TV_SHOW ||
    assetSubType?.toLowerCase() === ASSET_SUBTYPE.ORIGINALS
  ) {
    item.asset =
      assetSubType.toLowerCase() === ASSET_SUBTYPE.ORIGINALS
        ? ConsumptionPageUrls.ZEE5ORIGINALSCONSUMPTION
        : ConsumptionPageUrls.TVSHOWS_CONSUMPTION;
    url = showUrlGenerator(item);
  }
  return url;
};

const channelUrlGenerator = ({
  slug,
  web_url: url,
  title = '',
  id,
  seo_title: seoTitle = '',
  extended,
  channel_name: channelDetails,
  assetType,
  tags,
  ignoreChannelListing,
  start_time,
  end_time,
  channel,
  isChannelDetails
}: DynamicObject) => {
  const { title: channelName, id: channelId } = channelDetails || {
    title: null,
    id: null
  };

  if (!(title || seoTitle || extended || channelName) || !(id || channelId))
    return NOT_FOUND;

  const slugORUrl = slug || url;
  if (useSlug && slugORUrl) {
    return globalUrlHelper(`/${slugORUrl}`);
  }

  const extendedSeoTitle = extended ? extended.seo_title : '';
  const titleArray = [channelName, extendedSeoTitle, seoTitle, title];

  if (checkTitle(titleArray) && (id || channelId)) {
    const channelTitle = getTitle(titleArray);
    if (start_time && end_time) {
      const startTime = new Date(start_time).getTime() / 1000;
      const endTime = new Date(end_time).getTime() / 1000;
      return `/${ConsumptionPageUrls.CHANNELCONSUMPTION}/${channelTitle}/${channel.id}?start=${startTime}&end=${endTime}`;
    }
    const fromChannelLanding =
      !isServer() &&
      (window?.location?.pathname?.includes('/tv-channels') ||
        isChannelDetails);
    if (
      tags?.includes('channel_listing') &&
      !ignoreChannelListing &&
      (assetType === ASSET_TYPE.Channel || assetType === '9') &&
      fromChannelLanding
    ) {
      return globalUrlHelper(`/tv-shows/${channelTitle}/${id}`);
    }
    return globalUrlHelper(
      `/${ConsumptionPageUrls.CHANNELCONSUMPTION}/${channelTitle}/${
        channelId || id
      }`
    );
  }
  return NOT_FOUND;
};

export const joystickIconCheck = (item: DynamicObject) => {
  return item?.asset_type === 101 && 'gamify' in item ? true : false;
};

const asset101handler = (item: DynamicObject) => {
  //TODO: handle external assets navigation
  const { INTERNAL_LINK, EXTERNAL_LINK } = ExternalLinkEnums;
  const externalAsset = true;
  let link = '';
  const { genre, genres, gamify, webSlug, slug } = item;
  const isPlay5 = joystickIconCheck(item);
  const genresArray = genre || genres;
  let linkType =
    Array.isArray(genresArray) && typeof genresArray[0] === 'object'
      ? genresArray[0].value
      : '';
  let urlORSlug = '';
  if (isPlay5 && gamify.url) {
    urlORSlug = gamify.url;
  } else if (webSlug) {
    urlORSlug = webSlug;
  } else {
    urlORSlug = slug;
  }

  const isHipi = /.com\/hipi/.test(urlORSlug);
  if (isHipi) {
    linkType = EXTERNAL_LINK;
  }
  if (urlORSlug && !urlORSlug.startsWith('http')) {
    urlORSlug = `https://${urlORSlug}`;
  }

  // TODO: Refine this
  if (linkType === INTERNAL_LINK) {
    if (urlORSlug && urlORSlug.includes(OLD_CHANNELCONSUMPTION_PATH)) {
      const channelPath = urlORSlug.split('/');
      let channelName = '';
      let channelId = '';
      const channelPathLength = (channelPath && channelPath.length) || 0;
      if (channelPathLength > 7) {
        channelName = channelPath[6];
        channelId = channelPath[7];
      } else {
        channelName = channelPathLength > 5 ? channelPath[5] : '';
        channelId = channelPathLength > 6 ? channelPath[6] : '';
      }
      link =
        channelName && channelId ? `/live-tv/${channelName}/${channelId}` : '';
    } else {
      link = urlORSlug.includes(ZEE5_DOT_COM)
        ? urlORSlug.split(DOT_COM)[1]
        : urlORSlug;
    }
    /* In slug sometimes we receive an entire URL like
        https://www.zee5.com/tvshows/pathOfSpecificContent/ so handling it here in case of tvshows */
    if (link.includes('tvshows')) {
      const tempLinkArray = link.split('/');
      tempLinkArray[1] = ContentTypes.tvshows;
      link = tempLinkArray.join('/');
    }
    return globalUrlHelper(link);
  } else if (externalAsset) {
    return globalUrlHelper(urlORSlug);
  }
  return null;
};

const specialContentUrlGenerator = (
  item: SpecialContentItemType
): string | null => {
  if (item?.dynamicSlug) {
    return item.dynamicSlug;
  } else if (item?.link) {
    return item.link;
  }
  return null;
};

export const generateCardsUrl = (item: DynamicObject) => {
  // convert the object to camelCase
  item = transformToCamelCase(item, newKeysList);

  // // ILT nativeBridge implementation
  // if (event && indexObject && isWebView()) {
  //   handleActionForVideos(item.id);
  // }

  // // prevent default click button behaviour
  // if (event) {
  //   event.preventDefault();
  // }

  // const isEduauraa = getLocalStorage(PARTNER_NAME) === EDUAURAA;

  const { assetType } = item;
  // TODO: Integrate Play5 click
  // TODO: Hanldle for stories and ILT
  // // Stories handling
  // const isStories = isStoriesData(item);
  // if (isStories) {
  //   const link = item.permalink || item.link;
  //     return null;
  // } else if (assetType === undefined) {
  //   // TODO: undefined assets which are not stories
  // }

  // TODO: Handle tvod and tvod_downloadable

  switch (assetType) {
    case ASSET_TYPE.Movie:
      return movieAssetHandler(item);
    case ASSET_TYPE.TvShow:
      return showAssetHandler(item);
    case ASSET_TYPE.Episode:
      return episodeUrlGenerator(item);
    case ASSET_TYPE.Collection:
      return collectionUrlGenerator(item);
    case ASSET_TYPE.Channel: // TODO: some cases it is coming as string case '9':
    case ASSET_TYPE.EpgProgram:
      return channelUrlGenerator(item);
    case 101:
      return asset101handler(item);
    case 1001:
    default:
      return specialContentUrlGenerator(item);
  }
  return null;
};
/**
 * Checks whether the url is in PWA or webapp
 * @param slug url to be tested
 * @returns should redirect to pwa
 */
export const shouldRedirectToPWA = (slug: string) => {
  //TODO: Add more urls here when page  is added to webapp
  const consumptionPageURLs = [
    ConsumptionPageUrls.MOVIES_CONSUMPTION,
    ConsumptionPageUrls.KIDS_CONSUMPTION_MOVIES
    // Changes done for PWA2-18728 to be uncommented for internal redirection
    // ConsumptionPageUrls.TVSHOWS_CONSUMPTION,
    // ConsumptionPageUrls.ZEE5ORIGINALSCONSUMPTION
  ];
  //BellyBanner click is pointing to subscription page, which is also part of webapp, hence the excemption.
  const webAppURLs = [
    ...consumptionPageURLs,
    SubscriptionPageURL.SUBSCRIPTION_PAGE
  ];
  const result = !!webAppURLs.find((url) => slug.includes(url));
  return !result;
};

/**
 * Based on the url it will redirect to pwa or call the callback
 * @param slug Url required to be navigated
 * @param navigationCallback callback function
 */
export const navigationHandler = (
  slug: string,
  navigationCallback: (slug: string) => void
) => {
  const isEduauraa = getLocalStorage(PARTNER_NAME) === EDUAURAA;
  //TODO: Add plex consumption page conditions
  if (isEduauraa) return eduauraaAppLink(slug);
  //TODO: check if we need globalURLHelper here. Originally it was return navigationCallback(globalUrlHelper(slug, true));
  if (shouldRedirectToPWA(slug)) {
    return (location.href = slug);
  }
  return navigationCallback(slug);
};
